// "Ready to dig" composite readiness card on the Today tab.
//
// One row per active project. Six status pills with click-to-expand details.
// Top synthesis: READY (all green) / NOT READY (any red) / RUN (project
// already started). Pills use the standard Heritage gold/red palette.

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Project } from '../types'
import { navigateTo } from '../lib/nav-target'

type Color = 'green' | 'gold' | 'red' | 'gray'
type Synthesis = 'ready' | 'not_ready' | 'run'

type Rollup = Awaited<ReturnType<typeof api.projects.readinessRollup>>

const COLOR_BG: Record<Color, string> = {
  green: 'rgba(72, 167, 88, 0.18)',
  gold: 'rgba(212, 175, 55, 0.18)',
  red: 'rgba(204, 68, 68, 0.22)',
  gray: 'rgba(140, 140, 140, 0.15)',
}
const COLOR_BORDER: Record<Color, string> = {
  green: '#48a758',
  gold: '#d4af37',
  red: '#cc4444',
  gray: '#8c8c8c',
}

const SYNTH_LABEL: Record<Synthesis, string> = {
  ready: 'READY',
  not_ready: 'NOT READY',
  run: 'RUN',
}
const SYNTH_BG: Record<Synthesis, string> = {
  ready: 'rgba(72,167,88,0.25)',
  not_ready: 'rgba(204,68,68,0.30)',
  run: 'rgba(127,163,195,0.25)',
}

export default function ReadyToDigCard({ project }: { project: Project }) {
  const [data, setData] = useState<Rollup | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [startDateModal, setStartDateModal] = useState(false)
  const [startDateInput, setStartDateInput] = useState('')
  const [startDateSaving, setStartDateSaving] = useState(false)

  useEffect(() => {
    api.projects
      .readinessRollup(project.id)
      .then(setData)
      .catch((e) => setErr((e as Error).message))
  }, [project.id])

  async function saveStartDate() {
    if (!startDateInput) return
    setStartDateSaving(true)
    try {
      await api.projects.update(project.id, {
        start_date: startDateInput,
        status: 'active',
      } as Partial<Project>)
      const fresh = await api.projects.readinessRollup(project.id)
      setData(fresh)
      setStartDateModal(false)
      setStartDateInput('')
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setStartDateSaving(false)
    }
  }

  if (err) {
    return (
      <div className="card alert mb-3">
        <strong>Couldn't load readiness rollup for {project.name}:</strong> {err}
      </div>
    )
  }
  if (!data) return null

  const synth = data.synthesis as Synthesis

  function Pill(props: {
    id: string
    label: string
    value: string | number
    color: Color
  }) {
    const isOpen = expanded === props.id
    return (
      <button
        onClick={() => setExpanded(isOpen ? null : props.id)}
        style={{
          minWidth: 130,
          padding: '8px 12px',
          borderRadius: 6,
          background: COLOR_BG[props.color],
          borderLeft: `3px solid ${COLOR_BORDER[props.color]}`,
          border: '1px solid var(--border)',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
          fontFamily: 'inherit',
        }}
      >
        <div
          className="muted"
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {props.label}
        </div>
        <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>{props.value}</div>
      </button>
    )
  }

  return (
    <div className="card mb-3">
      {data.groundbreak_alert && (
        <button
          onClick={() => {
            setStartDateInput(new Date().toISOString().slice(0, 10))
            setStartDateModal(true)
          }}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            marginBottom: 12,
            padding: '8px 12px',
            background: COLOR_BG[data.groundbreak_alert.color as Color],
            borderLeft: `3px solid ${COLOR_BORDER[data.groundbreak_alert.color as Color]}`,
            border: '1px solid var(--border)',
            borderRadius: 4,
            cursor: 'pointer',
            color: 'inherit',
            fontFamily: 'inherit',
          }}
        >
          <strong>⚑ {data.groundbreak_alert.message}</strong>
          <div className="muted small mt-1">
            Target was {data.groundbreak_alert.target_date}. Click to confirm the real
            ground-break date and switch the project to Active.
          </div>
        </button>
      )}
      {startDateModal && (
        <div className="modal-bg" onClick={() => setStartDateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Set real ground-break date</h3>
            <p className="muted small mb-2">
              Target was {data.groundbreak_alert?.target_date}. Setting an actual date moves the
              project to Active and recomputes the schedule + decision deadlines from this start.
            </p>
            <label className="form-row">
              <span>Actual start date</span>
              <input
                type="date"
                autoFocus
                value={startDateInput}
                onChange={(e) => setStartDateInput(e.target.value)}
              />
            </label>
            <div className="row gap-2 mt-3">
              <button
                className="btn-primary"
                onClick={saveStartDate}
                disabled={startDateSaving || !startDateInput}
              >
                {startDateSaving ? 'Saving…' : 'Set start date'}
              </button>
              <button className="btn-ghost" onClick={() => setStartDateModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="row-between mb-2" style={{ alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0 }}>
            Ready to dig · {project.name}
          </h3>
        </div>
        <span
          style={{
            padding: '4px 10px',
            borderRadius: 4,
            background: SYNTH_BG[synth],
            fontSize: '0.85rem',
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          {SYNTH_LABEL[synth]}
        </span>
      </div>
      <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <Pill id="permits" label="Permits" value={data.pills.permits.value} color={data.pills.permits.color} />
        <Pill id="subs_ready" label="Subs ready" value={data.pills.subs_ready.value} color={data.pills.subs_ready.color} />
        <Pill id="long_leads" label="Long-leads" value={data.pills.long_leads.value} color={data.pills.long_leads.color} />
        <Pill id="decisions" label="Decisions" value={data.pills.decisions.value} color={data.pills.decisions.color} />
        <Pill id="spec_compliance" label="Spec compliance" value={data.pills.spec_compliance.value} color={data.pills.spec_compliance.color} />
        <Pill id="weather" label="Weather" value={data.pills.weather.value} color={data.pills.weather.color} />
      </div>
      {expanded && (
        <div className="mt-2 small" style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          {expanded === 'permits' && (
            <PillDetail
              detail={data.pills.permits.detail as { compliance: any[]; scope: any[]; blocking_soon: number }}
              renderItem={(comp, scope) => {
                if (comp.length + scope.length === 0) return <em>No permit items tracked.</em>
                return (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {comp.map((c: any) => (
                      <li key={`c-${c.id}`}>
                        {c.completed ? '✓ ' : '○ '}
                        {c.title} <span className="muted">— due {c.due_date}</span>
                      </li>
                    ))}
                    {scope.map((s: any) => (
                      <li key={`s-${s.id}`}>
                        {s.status === 'complete' ? '✓ ' : '○ '}
                        {s.scope_item}
                      </li>
                    ))}
                  </ul>
                )
              }}
            />
          )}
          {expanded === 'subs_ready' && (
            <SubsDetail detail={data.pills.subs_ready.detail as { not_ready: any[] }} />
          )}
          {expanded === 'long_leads' && (
            <LongLeadDetail detail={data.pills.long_leads.detail as { items: any[]; past_due_count: number }} />
          )}
          {expanded === 'decisions' && (
            <DecisionsDetail detail={data.pills.decisions.detail as { items: any[]; overdue_count: number }} />
          )}
          {expanded === 'spec_compliance' && (
            <SpecDetail detail={data.pills.spec_compliance.detail as { items: any[]; violation_count: number }} />
          )}
          {expanded === 'weather' && (
            <WeatherDetail detail={data.pills.weather.detail as { critical: any[]; all_forecasts: any[] }} />
          )}
        </div>
      )}
    </div>
  )
}

function PillDetail({
  detail,
  renderItem,
}: {
  detail: { compliance: any[]; scope: any[]; blocking_soon: number }
  renderItem: (comp: any[], scope: any[]) => React.ReactNode
}) {
  return (
    <div>
      {detail.blocking_soon > 0 && (
        <div className="muted" style={{ marginBottom: 6 }}>
          {detail.blocking_soon} permit(s) due within 14 days, not yet complete.
        </div>
      )}
      {renderItem(detail.compliance, detail.scope)}
    </div>
  )
}

function SubsDetail({ detail }: { detail: { not_ready: any[] } }) {
  if (detail.not_ready.length === 0) return <em>All subs on this project are ready.</em>
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {detail.not_ready.map((s) => (
        <li key={s.sub_id}>
          <button
            className="link"
            onClick={() => navigateTo({ tab: 'subs' })}
            style={{ fontSize: 'inherit' }}
          >
            {s.company_name}
          </button>
          <span className="muted"> — missing: {s.missing.join(', ')}</span>
        </li>
      ))}
    </ul>
  )
}

function LongLeadDetail({ detail }: { detail: { items: any[]; past_due_count: number } }) {
  if (detail.items.length === 0) return <em>No long-lead items in next 7 days.</em>
  return (
    <div>
      {detail.past_due_count > 0 && (
        <div className="muted" style={{ marginBottom: 6 }}>
          {detail.past_due_count} item(s) past their need-by date.
        </div>
      )}
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {detail.items.map((m) => {
          const deadline = m.need_by_date ?? m.eta_date
          const lbl = m.need_by_date ? 'need-by' : 'ETA'
          const status = m.ordered_date ? `ordered ${m.ordered_date}` : 'not ordered'
          return (
            <li key={m.id}>
              <button
                className="link"
                onClick={() => navigateTo({ tab: 'materials' })}
                style={{ fontSize: 'inherit' }}
              >
                {m.item}
              </button>
              <span className="muted">
                {' '}— {m.vendor ?? '?'} · {lbl} {deadline} · {status}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function DecisionsDetail({
  detail,
}: {
  detail: { items: any[]; overdue_count: number; awaiting_schedule?: boolean }
}) {
  if (detail.awaiting_schedule)
    return (
      <em>
        Awaiting schedule. Decision deadlines compute from the project start date — set a
        ground-break date to populate them.
      </em>
    )
  if (detail.items.length === 0) return <em>No decisions due in next 14 days.</em>
  return (
    <div>
      {detail.overdue_count > 0 && (
        <div className="muted" style={{ marginBottom: 6 }}>
          {detail.overdue_count} decision(s) past deadline.
        </div>
      )}
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {detail.items.map((d) => (
          <li key={d.id}>
            <button
              className="link"
              onClick={() => navigateTo({ tab: 'decisions' })}
              style={{ fontSize: 'inherit' }}
            >
              {d.decision_name}
            </button>
            <span className="muted"> — {d.phase_needed_for ?? '?'} · deadline {d.deadline}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SpecDetail({ detail }: { detail: { items: any[]; violation_count: number } }) {
  if (detail.items.length === 0) return <em>No open spec compliance flags.</em>
  return (
    <div>
      {detail.violation_count > 0 && (
        <div className="muted" style={{ marginBottom: 6 }}>
          {detail.violation_count} violation(s) — material plan-vs-spec deviations.
        </div>
      )}
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {detail.items.map((f) => (
          <li key={f.id}>
            <button
              className="link"
              onClick={() => navigateTo({ tab: 'scope' })}
              style={{ fontSize: 'inherit' }}
            >
              [{f.severity}] {f.category}
            </button>
            {f.spec_value && <span className="muted"> — spec: {String(f.spec_value).slice(0, 80)}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

function WeatherDetail({ detail }: { detail: { critical: any[]; all_forecasts: any[] } }) {
  if (detail.all_forecasts.length === 0)
    return <em>No forecast loaded — geocode the project on Settings.</em>
  if (detail.critical.length === 0)
    return <em>No critical weather windows in next 7 days.</em>
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {detail.critical.map((f) => (
        <li key={f.forecast_date}>
          {f.forecast_date}: {f.short_forecast}{' '}
          <span className="muted">
            ({f.high_temp_f ?? '—'}° / {f.low_temp_f ?? '—'}°
            {f.precipitation_chance != null && f.precipitation_chance > 0
              ? ` · ${f.precipitation_chance}% precip`
              : ''}
            {f.wind_speed_mph != null && f.wind_speed_mph >= 20 ? ` · wind ${f.wind_speed_mph}mph` : ''}
            )
          </span>
        </li>
      ))}
    </ul>
  )
}
